// ============================================================
// Word Dictionary — maps words to spawnable entity configs
// ============================================================
// Each entry describes what gets created when the player types a word.
//
// Fields:
//   type       : 'platform'|'vehicle'|'creature'|'item'|'hazard'|'food'|'tool'
//   shape      : 'rect'|'circle'
//   w, h       : default dimensions (px)
//   color      : PIXI hex color
//   mass       : relative kg
//   bounciness : 0–2 (>1 = super bouncy)
//   friction   : 0–1
//   isStatic   : stays in place
//   noGravity  : ignores gravity
//   gravityScale: multiplier on gravity (negative = floats)
//   canFloat   : buoyant in water
//   material   : 'default'|'ice'|'bouncy'|'sticky'|'lava'|'water'
//   ai         : null|'helper'|'enemy'|'wander'
//   special    : list of special behaviors
//   description: shown in UI
//   emoji      : for HUD display

export const WORD_DICTIONARY = {
  // ── Platforms / Structures ──────────────────────────────
  ladder: {
    type: 'platform', shape: 'rect',
    w: 40, h: 200, color: 0x8B4513, mass: 10,
    isStatic: false, canFloat: false,
    bounciness: 0, friction: 0.9,
    special: ['climbable'],
    description: 'A wooden ladder to climb up!',
    emoji: '🪜',
  },
  bridge: {
    type: 'platform', shape: 'rect',
    w: 260, h: 24, color: 0x7B5E44, mass: 15,
    isStatic: false, canFloat: true,
    bounciness: 0, friction: 0.85,
    special: [],
    description: 'A sturdy wooden bridge.',
    emoji: '🌉',
  },
  plank: {
    type: 'platform', shape: 'rect',
    w: 140, h: 18, color: 0xA0724A, mass: 4,
    isStatic: false, canFloat: true,
    bounciness: 0, friction: 0.85,
    special: [],
    description: 'A simple wooden plank.',
    emoji: '🪵',
  },
  trampoline: {
    type: 'platform', shape: 'rect',
    w: 120, h: 26, color: 0xFF6B6B, mass: 5,
    isStatic: false, canFloat: false,
    bounciness: 1.8, friction: 0.5,
    material: 'bouncy',
    special: ['bouncy'],
    description: 'Boing boing! Super bouncy!',
    emoji: '🤸',
  },
  spring: {
    type: 'platform', shape: 'rect',
    w: 50, h: 30, color: 0xFFEB3B, mass: 2,
    isStatic: true, canFloat: false,
    bounciness: 2.2, friction: 0.5,
    material: 'bouncy',
    special: ['bouncy'],
    description: 'Launches you into the air!',
    emoji: '🌀',
  },
  cloud: {
    type: 'platform', shape: 'rect',
    w: 160, h: 50, color: 0xF0F8FF, mass: 1,
    isStatic: false, canFloat: false,
    noGravity: true, bounciness: 0.1,
    friction: 0.5,
    special: ['floaty'],
    description: 'A fluffy cloud to stand on.',
    emoji: '☁️',
  },
  shelf: {
    type: 'platform', shape: 'rect',
    w: 100, h: 18, color: 0x795548, mass: 8,
    isStatic: true, canFloat: false,
    bounciness: 0, friction: 0.9,
    special: [],
    description: 'A fixed wooden shelf.',
    emoji: '📦',
  },
  ice: {
    type: 'platform', shape: 'rect',
    w: 150, h: 22, color: 0xADD8E6, mass: 6,
    isStatic: false, canFloat: true,
    bounciness: 0.1, friction: 0.05,
    material: 'ice',
    special: ['slippery'],
    description: 'Super slippery ice!',
    emoji: '🧊',
  },
  // ── Creatures / NPCs ────────────────────────────────────
  robot: {
    type: 'creature', shape: 'rect',
    w: 55, h: 80, color: 0x90A4AE, mass: 8,
    isStatic: false, canFloat: false,
    bounciness: 0.1, friction: 0.8,
    ai: 'helper',
    special: ['npc', 'carries_player'],
    description: 'A helpful robot friend!',
    emoji: '🤖',
  },
  dragon: {
    type: 'creature', shape: 'rect',
    w: 120, h: 90, color: 0x66BB6A, mass: 30,
    isStatic: false, canFloat: false,
    bounciness: 0, friction: 0.8,
    ai: 'friendly',
    special: ['npc', 'rideable', 'flies'],
    description: 'A friendly dragon! Hop on!',
    emoji: '🐉',
  },
  turtle: {
    type: 'creature', shape: 'circle',
    w: 60, h: 60, color: 0x4CAF50, mass: 5,
    isStatic: false, canFloat: true,
    bounciness: 0.3, friction: 0.7,
    ai: 'wander',
    special: ['npc', 'friendly'],
    description: 'Another turtle friend! Hello!',
    emoji: '🐢',
  },
  crab: {
    type: 'creature', shape: 'rect',
    w: 60, h: 40, color: 0xEF5350, mass: 3,
    isStatic: false, canFloat: false,
    bounciness: 0.2, friction: 0.8,
    ai: 'wander',
    special: ['npc'],
    description: 'A wandering crab.',
    emoji: '🦀',
  },
  fish: {
    type: 'creature', shape: 'rect',
    w: 50, h: 35, color: 0x42A5F5, mass: 1,
    isStatic: false, canFloat: true,
    bounciness: 0.2, friction: 0.5,
    noGravity: false, gravityScale: 0,
    ai: 'wander',
    special: ['npc', 'water_only'],
    description: 'A friendly fish!',
    emoji: '🐠',
  },
  // ── Vehicles ────────────────────────────────────────────
  boat: {
    type: 'vehicle', shape: 'rect',
    w: 180, h: 60, color: 0x8D6E63, mass: 20,
    isStatic: false, canFloat: true,
    bounciness: 0.05, friction: 0.85,
    special: ['rideable', 'floats'],
    description: 'A sturdy wooden boat!',
    emoji: '⛵',
  },
  raft: {
    type: 'vehicle', shape: 'rect',
    w: 140, h: 28, color: 0xA1887F, mass: 10,
    isStatic: false, canFloat: true,
    bounciness: 0.05, friction: 0.9,
    special: ['rideable', 'floats'],
    description: 'A simple log raft.',
    emoji: '🪵',
  },
  helicopter: {
    type: 'vehicle', shape: 'rect',
    w: 100, h: 60, color: 0x78909C, mass: 15,
    isStatic: false, canFloat: false,
    bounciness: 0, friction: 0.7,
    noGravity: true,
    special: ['rideable', 'flies', 'propelled'],
    description: 'A helicopter! Flies anywhere!',
    emoji: '🚁',
  },
  // ── Items / Tools ────────────────────────────────────────
  balloon: {
    type: 'item', shape: 'circle',
    w: 55, h: 70, color: 0xFF80AB, mass: 0.3,
    isStatic: false, canFloat: false,
    bounciness: 0.5, friction: 0.4,
    gravityScale: -0.04,   // very gentle float upward
    special: ['floaty', 'lift'],
    description: 'Floats up! Attach to things!',
    emoji: '🎈',
  },
  key: {
    type: 'item', shape: 'rect',
    w: 30, h: 50, color: 0xFFD700, mass: 0.5,
    isStatic: false, canFloat: false,
    bounciness: 0.2, friction: 0.8,
    special: ['key', 'pickupable'],
    description: 'Opens locked doors!',
    emoji: '🗝️',
  },
  torch: {
    type: 'item', shape: 'rect',
    w: 20, h: 50, color: 0xFF8F00, mass: 0.5,
    isStatic: false, canFloat: false,
    bounciness: 0.1, friction: 0.8,
    special: ['light', 'fire', 'pickupable'],
    description: 'Lights up dark areas!',
    emoji: '🔦',
  },
  rope: {
    type: 'item', shape: 'rect',
    w: 20, h: 150, color: 0xD7CCC8, mass: 1,
    isStatic: false, canFloat: false,
    bounciness: 0.1, friction: 0.9,
    special: ['climbable', 'swingable'],
    description: 'Climb up or swing across!',
    emoji: '🪢',
  },
  magnet: {
    type: 'item', shape: 'rect',
    w: 50, h: 50, color: 0xF44336, mass: 3,
    isStatic: false, canFloat: false,
    bounciness: 0.1, friction: 0.8,
    special: ['magnetic', 'attracts_metal'],
    description: 'Attracts metal objects!',
    emoji: '🧲',
  },
  // ── Food ────────────────────────────────────────────────
  pizza: {
    type: 'food', shape: 'circle',
    w: 60, h: 60, color: 0xFF8F00, mass: 0.8,
    isStatic: false, canFloat: false,
    bounciness: 0.1, friction: 0.7,
    special: ['heal', 'tasty'],
    description: 'Restores energy! Nom nom!',
    emoji: '🍕',
  },
  cake: {
    type: 'food', shape: 'rect',
    w: 80, h: 60, color: 0xF48FB1, mass: 1,
    isStatic: false, canFloat: false,
    bounciness: 0.15, friction: 0.7,
    special: ['heal', 'tasty'],
    description: 'Delicious cake! Restores energy!',
    emoji: '🎂',
  },
  seaweed: {
    type: 'food', shape: 'rect',
    w: 30, h: 80, color: 0x388E3C, mass: 0.3,
    isStatic: true, canFloat: false,
    noGravity: true,
    bounciness: 0, friction: 0.9,
    special: ['sticky', 'slows'],
    description: 'Seaweed! Slows things down.',
    emoji: '🌿',
  },
  // ── Hazards ─────────────────────────────────────────────
  bomb: {
    type: 'hazard', shape: 'circle',
    w: 50, h: 50, color: 0x424242, mass: 4,
    isStatic: false, canFloat: false,
    bounciness: 0.3, friction: 0.6,
    special: ['explosive', 'timer_3s'],
    description: '💥 Watch out! It explodes!',
    emoji: '💣',
  },
  fire: {
    type: 'hazard', shape: 'rect',
    w: 60, h: 80, color: 0xFF5722, mass: 0.1,
    isStatic: true, canFloat: false,
    noGravity: true,
    bounciness: 0, friction: 0,
    special: ['fire', 'damage', 'spreads'],
    description: 'Hot! Damages anything that touches it!',
    emoji: '🔥',
  },
  rock: {
    type: 'hazard', shape: 'circle',
    w: 70, h: 70, color: 0x607D8B, mass: 20,
    isStatic: false, canFloat: false,
    bounciness: 0.15, friction: 0.7,
    special: [],
    description: 'A heavy boulder.',
    emoji: '🪨',
  },
  // ── Environment ─────────────────────────────────────────
  box: {
    type: 'platform', shape: 'rect',
    w: 60, h: 60, color: 0xFFB74D, mass: 5,
    isStatic: false, canFloat: true,
    bounciness: 0.1, friction: 0.8,
    special: ['pushable'],
    description: 'A wooden box. Push it around!',
    emoji: '📦',
  },
  barrel: {
    type: 'platform', shape: 'circle',
    w: 55, h: 70, color: 0x6D4C41, mass: 8,
    isStatic: false, canFloat: true,
    bounciness: 0.3, friction: 0.6,
    special: ['rollable'],
    description: 'A barrel. It rolls!',
    emoji: '🛢️',
  },
  gem: {
    type: 'item', shape: 'circle',
    w: 35, h: 35, color: 0x00BCD4, mass: 0.5,
    isStatic: false, canFloat: false,
    bounciness: 0.5, friction: 0.5,
    special: ['collectible', 'score_100'],
    description: 'A shiny gem! Collect it!',
    emoji: '💎',
  },
  pearl: {
    type: 'item', shape: 'circle',
    w: 30, h: 30, color: 0xF5F5F5, mass: 0.3,
    isStatic: false, canFloat: false,
    bounciness: 0.6, friction: 0.4,
    special: ['collectible', 'score_200'],
    description: 'A rare pearl! Very valuable!',
    emoji: '🫧',
  },
  star: {
    type: 'item', shape: 'circle',
    w: 40, h: 40, color: 0xFFD700, mass: 0.1,
    isStatic: true, noGravity: true,
    bounciness: 0, friction: 0,
    special: ['goal'],
    description: 'The goal! Reach it!',
    emoji: '⭐',
  },
};

// ── Modifiers ───────────────────────────────────────────────
export const MODIFIERS = {
  giant:     { scaleW: 3.0, scaleH: 3.0, scaleMass: 8,   label: 'GIANT'     },
  huge:      { scaleW: 2.0, scaleH: 2.0, scaleMass: 4,   label: 'HUGE'      },
  tiny:      { scaleW: 0.3, scaleH: 0.3, scaleMass: 0.1, label: 'TINY'      },
  mini:      { scaleW: 0.5, scaleH: 0.5, scaleMass: 0.3, label: 'MINI'      },
  heavy:     { scaleMass: 10,  extraFriction: 0.1,       label: 'HEAVY'     },
  light:     { scaleMass: 0.1, scaleGravity: 0.5,        label: 'LIGHT'     },
  bouncy:    { bounciness: 1.8, addSpecial: ['bouncy'],  label: 'BOUNCY'    },
  sticky:    { material: 'sticky', friction: 0.05,       label: 'STICKY'    },
  flying:    { gravityScale: -0.04, addSpecial: ['flies'], label: 'FLYING'   },
  frozen:    { material: 'ice', color: 0xB0E0FF,         label: 'FROZEN'    },
  electric:  { addSpecial: ['electric'], color: 0xFFFF00, label: 'ELECTRIC' },
  explosive: { addSpecial: ['explosive'], color: 0xFF4500,label: 'EXPLOSIVE'},
  sleepy:    { ai: 'idle',                               label: 'SLEEPY'    },
  friendly:  { ai: 'helper', addSpecial: ['friendly'],   label: 'FRIENDLY'  },
  angry:     { ai: 'enemy',  addSpecial: ['angry'],      label: 'ANGRY'     },
  invisible: { alpha: 0.15,                              label: 'INVISIBLE' },
  rainbow:   { rainbow: true,                            label: 'RAINBOW'   },
};

/**
 * Parse a player's input string into { modifiers[], word }.
 * E.g. "giant bouncy trampoline" → { modifiers:['giant','bouncy'], word:'trampoline' }
 */
export function parseWordInput(input) {
  const tokens = input.toLowerCase().trim().split(/\s+/);
  const modifiers = [];
  let word = null;

  for (const token of tokens) {
    if (MODIFIERS[token]) {
      modifiers.push(token);
    } else if (WORD_DICTIONARY[token]) {
      word = token;
    } else {
      // Try fuzzy match
      const fuzzy = fuzzyFind(token, Object.keys(WORD_DICTIONARY));
      if (fuzzy) word = fuzzy;
      else {
        const fuzzyMod = fuzzyFind(token, Object.keys(MODIFIERS));
        if (fuzzyMod) modifiers.push(fuzzyMod);
      }
    }
  }

  return { modifiers, word };
}

/**
 * Build a merged entity config from a word + modifiers.
 */
export function buildEntityConfig(word, modifierNames) {
  const base = WORD_DICTIONARY[word];
  if (!base) return null;

  const config = {
    ...base,
    special: [...(base.special || [])],
    alpha: 1,
  };

  for (const modName of modifierNames) {
    const mod = MODIFIERS[modName];
    if (!mod) continue;

    if (mod.scaleW)        { config.w = Math.round(config.w * mod.scaleW); }
    if (mod.scaleH)        { config.h = Math.round(config.h * mod.scaleH); }
    if (mod.scaleMass)     { config.mass = config.mass * mod.scaleMass; }
    if (mod.scaleGravity != null) { config.gravityScale = (config.gravityScale ?? 1) * mod.scaleGravity; }
    if (mod.bounciness != null)   { config.bounciness = mod.bounciness; }
    if (mod.material)      { config.material = mod.material; }
    if (mod.color)         { config.color = mod.color; }
    if (mod.alpha != null) { config.alpha = mod.alpha; }
    if (mod.ai)            { config.ai = mod.ai; }
    if (mod.gravityScale != null) { config.gravityScale = mod.gravityScale; }
    if (mod.addSpecial)    { config.special.push(...mod.addSpecial); }
    if (mod.friction != null) { config.friction = mod.friction; }
    if (mod.rainbow)       { config.rainbow = true; }
    if (mod.extraFriction) { config.friction = (config.friction ?? 0.8) + mod.extraFriction; }
    config.modLabel = (config.modLabel ? config.modLabel + ' ' : '') + mod.label;
  }

  return config;
}

/** Simple Levenshtein-based fuzzy find (max edit distance 2) */
function fuzzyFind(input, options) {
  let best = null;
  let bestDist = 3;
  for (const opt of options) {
    const dist = levenshtein(input, opt);
    if (dist < bestDist) {
      bestDist = dist;
      best = opt;
    }
  }
  return best;
}

function levenshtein(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => i === 0 ? j : j === 0 ? i : 0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i-1] === b[j-1]
        ? dp[i-1][j-1]
        : 1 + Math.min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]);
    }
  }
  return dp[m][n];
}
